import React from 'react';
import { Scenario, ScenarioStep, SupplyShedDriveTime } from '../../lib/types';
import { SCENARIOS } from '../../lib/scenarios';
import { ScenarioStepIndicator } from './ScenarioStepIndicator';
import { ScenarioInteractionHint } from './ScenarioInteractionHint';
import { ScenarioInsightCard } from './ScenarioInsightCard';
import { ScenarioCard } from '../shared/ScenarioCard';
import { LucaMiniChart } from './scenarioAux/LucaMiniChart';
import {
  usePerCrownNativeWindow,
  hasPerCrownCoverage,
} from '../../hooks/usePerCrownNativeWindow';

interface ScenarioPanelProps {
  scenario: Scenario;
  step: ScenarioStep;
  stepIndex: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  millCode: string | null;
  driveTime: SupplyShedDriveTime;
  onPrev: () => void;
  onNext: () => void;
  // Two distinct off-ramps. `onExit` ends the scenario but stays on the
  // current mill (free exploration of the same shed). `onExitToExplorer`
  // ends the scenario AND navigates back to the supply-shed picker.
  // Disambiguating these makes the end-of-scenario decision explicit
  // instead of forcing a guess about user intent.
  onExit: () => void;
  onExitToExplorer: () => void;
  onJumpToStep: (i: number) => void;
  // Native-resolution per-crown drill-down — same boolean + toggle that
  // PerCrownContent's left-panel button drives. Optional so existing
  // scenarios that don't reach the per-tree insight can omit them.
  // When the active step's insight is `per_tree_carbon_rollup` AND the
  // (mill, drive-time) tuple has v1.2 native-window coverage, the panel
  // renders a CTA tile that fires the same camera fly + overlay activation.
  nativeViewActive?: boolean;
  onToggleNativeView?: () => void;
}

export const ScenarioPanel: React.FC<ScenarioPanelProps> = ({
  scenario,
  step,
  stepIndex,
  totalSteps,
  isFirstStep,
  isLastStep,
  millCode,
  driveTime,
  onPrev,
  onNext,
  onExit,
  onExitToExplorer,
  onJumpToStep,
  nativeViewActive,
  onToggleNativeView,
}) => {
  // Sentinel Creek native-window probe — short-circuits to no-fetch for
  // mills outside the v1.2 coverage set, so this hook is a no-op for every
  // scenario except `individual_tree`. Reuses the same fetch path as
  // PerCrownContent + DetailMapView so `fetchWithCache` dedupes to a
  // single network round-trip.
  const nativeWindow = usePerCrownNativeWindow(
    millCode,
    driveTime,
    hasPerCrownCoverage(millCode),
  );
  const showNativeViewCta =
    step.insight === 'per_tree_carbon_rollup' &&
    nativeWindow.data !== null &&
    onToggleNativeView !== undefined;

  return (
    <div className="space-y-4">
      {/* Header: scenario identity + Exit affordance. The header link is
          hidden on the final (insight) step because the footer already
          provides two explicit off-ramps ("Stay & explore" + "Back to
          supply sheds"). Showing both at once was a competing-CTA UX bug —
          users couldn't tell whether the header link did the same thing as
          the footer's Stay or the footer's Back. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-1">
          <p className="text-xs font-roboto-mono text-neutral-500 uppercase tracking-wider">
            Scenario
          </p>
          <h3 className="text-title-md font-roboto font-bold text-figma-text flex items-center gap-2">
            <span aria-hidden="true">{scenario.emoji}</span>
            <span>{scenario.name}</span>
          </h3>
          <p className="text-body-sm font-roboto text-neutral-400">{scenario.sector}</p>
        </div>
        {!isLastStep && (
          <button
            type="button"
            onClick={onExit}
            className="text-body-sm font-roboto text-neutral-400 hover:text-figma-yellow transition-colors whitespace-nowrap mt-1"
            aria-label="End scenario and stay on this mill in free exploration"
          >
            End scenario
          </button>
        )}
      </div>

      <ScenarioStepIndicator
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        onJumpToStep={onJumpToStep}
      />

      {/* Step subtitle — short, scannable context above the longer
          narrative. Optional so existing scenarios without a title fall
          back gracefully to the narrative-only layout. */}
      {step.title && (
        <h4 className="text-title-md font-roboto font-bold text-figma-text leading-snug">
          {step.title}
        </h4>
      )}

      {/* Narrative — the docent voice. */}
      <p className="text-body font-roboto text-neutral-200 leading-relaxed">
        {step.narrative}
      </p>

      {step.interaction && <ScenarioInteractionHint interaction={step.interaction} />}

      {/* Auxiliary inline chart — surfaces a supporting time series alongside
          a spatial layer the step is showing on the map. Currently only the
          LUCA monthly bar chart is wired (Carbon Offense step 2 — recent
          disturbance pulse paired with the AGB change layer). */}
      {step.auxiliaryChart === 'luca_monthly' && (
        <LucaMiniChart millCode={millCode} driveTime={driveTime} />
      )}

      {showNativeViewCta && nativeWindow.data && (
        <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-yellow/40 shadow-figma-card">
          <p className="text-xs font-roboto-mono text-figma-yellow uppercase tracking-wider">
            Individual trees
            <span className="ml-1 text-neutral-400 normal-case tracking-normal">
              · {nativeWindow.data.contextBoundary?.name ?? 'native resolution'}
            </span>
          </p>
          <p className="mt-2 text-body-sm font-roboto text-neutral-300 leading-relaxed">
            {nativeWindow.data.crownCount.toLocaleString()} crown polygons at
            native 60 cm resolution
            {nativeWindow.data.contextBoundary
              ? ` inside ${nativeWindow.data.contextBoundary.name}`
              : ' in the densest 1 km² of the shed'}
            .{' '}
            <span className="md:hidden">Pinch to zoom in, then tap any tree to see its attributes.</span>
            <span className="hidden md:inline">Hover any tree on the map to see its attributes.</span>
          </p>
          <button
            type="button"
            onClick={onToggleNativeView}
            className={`mt-3 w-full px-3 py-2 rounded-figma-panel font-roboto-mono text-xs uppercase tracking-wider transition-colors ${
              nativeViewActive
                ? 'bg-figma-card border border-figma-yellow text-figma-yellow hover:bg-figma-yellow/10'
                : 'bg-figma-yellow text-figma-panel hover:bg-figma-yellow/85'
            }`}
          >
            {nativeViewActive ? 'Exit native resolution view' : 'View individual trees →'}
          </button>
        </div>
      )}

      {step.insight && (
        // `data-scenario-insight` is the scroll target for the mobile
        // map overlay's "Open insight ↗" button — DetailView selects on
        // this attribute and `scrollIntoView`s after the sheet expands,
        // so the user lands on the insight card instead of the panel
        // top.
        <div data-scenario-insight>
        <ScenarioInsightCard
          insightId={step.insight}
          millCode={millCode}
          driveTime={driveTime}
        />
        </div>
      )}

      {/* On the insight (final) step, resurface the other scenarios so the
          user can hop straight from one payoff into the next without having
          to navigate back to ExplorerView. Clicking a card does the same
          navigate-with-state handoff as the ExplorerView selector — React
          Router unmounts this DetailView and remounts against the new mill,
          which kicks off the new scenario cleanly. */}
      {isLastStep && (
        <div className="pt-2 space-y-2">
          <p className="text-xs font-roboto-mono text-neutral-500 uppercase tracking-wider">
            Try another scenario
          </p>
          <div className="space-y-2">
            {SCENARIOS.filter((s) => s.id !== scenario.id).map((s) => (
              <ScenarioCard key={s.id} scenario={s} variant="compact" />
            ))}
          </div>
        </div>
      )}

      {/* Footer. Mid-scenario this is Prev / Next. On the last (insight)
          step it splits into two explicit off-ramps — "Stay & explore"
          stays on the current mill in free mode (matches the header's End
          scenario link), "Back to supply sheds" ends the scenario AND
          navigates to the picker. The two-button split disambiguates
          intent at the moment it actually matters; mid-scenario the
          header link is enough. */}
      {isLastStep ? (
        <div className="space-y-2 pt-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPrev}
              disabled={isFirstStep}
              className="flex-1 px-4 py-2 rounded-figma-panel border border-figma-lime/30 bg-figma-panel text-body-sm font-roboto text-neutral-200 hover:border-figma-lime/60 hover:text-figma-text disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ← Prev
            </button>
            <button
              type="button"
              onClick={onExit}
              className="flex-1 px-4 py-2 rounded-figma-panel border border-figma-lime/30 bg-figma-panel text-body-sm font-roboto text-neutral-200 hover:border-figma-lime/60 hover:text-figma-text transition-colors"
            >
              Stay & explore
            </button>
          </div>
          <button
            type="button"
            onClick={onExitToExplorer}
            className="w-full px-4 py-2 rounded-figma-panel bg-figma-yellow text-body-sm font-roboto font-bold text-figma-panel hover:bg-figma-yellow/90 transition-colors"
          >
            Back to supply sheds
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={onPrev}
            disabled={isFirstStep}
            className="flex-1 px-4 py-2 rounded-figma-panel border border-figma-lime/30 bg-figma-panel text-body-sm font-roboto text-neutral-200 hover:border-figma-lime/60 hover:text-figma-text disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            ← Prev
          </button>
          <button
            type="button"
            onClick={onNext}
            className="flex-1 px-4 py-2 rounded-figma-panel bg-figma-yellow text-body-sm font-roboto font-bold text-figma-panel hover:bg-figma-yellow/90 transition-colors"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};
